// Cookie utilities for user tracking and data persistence
import { v4 as uuidv4 } from 'uuid';
import { SearchRequest } from '../types';

export interface UserData {
    userId: string;
    lastSearch?: Partial<SearchRequest>;
    consentGiven: boolean;
    consentDate?: string;
}

// Generate a unique GUID using uuid library
export const generateGUID = (): string => {
    return uuidv4();
};

// Cookie management functions
export const setCookie = (name: string, value: string, days: number): void => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
};

export const getCookie = (name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
};

export const deleteCookie = (name: string): void => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// User data management
export const getUserData = (): UserData | null => {
    const userData = getCookie('osmap_user_data');
    if (!userData) return null;
    
    try {
        return JSON.parse(decodeURIComponent(userData));
    } catch (error) {
        console.error('Error parsing user data cookie:', error);
        return null;
    }
};

export const setUserData = (userData: UserData): void => {
    const encodedData = encodeURIComponent(JSON.stringify(userData));
    setCookie('osmap_user_data', encodedData, 180); // 180 days
};

export const initializeUser = (): UserData => {
    let userData = getUserData();
    
    if (!userData || !userData.consentGiven) {
        // Create new user data but don't save until consent is given
        userData = {
            userId: generateGUID(),
            consentGiven: false
        };
    } else {
        // Refresh existing user data (extends cookie expiration)
        setUserData(userData);
    }
    
    return userData;
};

export const saveSearchData = (searchRequest: Partial<SearchRequest>): void => {
    const userData = getUserData();
    if (!userData || !userData.consentGiven) return;
    
    const updatedUserData: UserData = {
        ...userData,
        lastSearch: {
            starting_post_code: searchRequest.starting_post_code,
            miles_radius: searchRequest.miles_radius,
            residential_needs: searchRequest.residential_needs,
            dementia_needs: searchRequest.dementia_needs,
            nursing_needs: searchRequest.nursing_needs,
            sort: searchRequest.sort,
            fundingType: searchRequest.fundingType
        }
    };
    
    setUserData(updatedUserData);
};

export const clearAllCookies = (): void => {
    deleteCookie('osmap_user_data');
    deleteCookie('osmap_consent');
};

// Check if cookies are accepted
export const hasConsentForCookies = (): boolean => {
    const userData = getUserData();
    return userData?.consentGiven === true;
};

// Long-lived first-party cookie used by the public Submit-event form
// (update 2026-05-05.1). Drives the backend per-cookie 10/h rate limiter and
// is intentionally separate from osmap_user_data because the form needs the
// id even when the user has not (yet) given cookie consent on the main site.
const HOME_BROWSER_ID_COOKIE = 'home_browser_id';
const HOME_BROWSER_ID_DAYS = 730; // ~2 years

const isValidGuid = (value: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const getOrCreateHomeBrowserId = (): string => {
    const existing = getCookie(HOME_BROWSER_ID_COOKIE);
    if (existing && isValidGuid(existing)) {
        // Refresh the expiry on every read so the cookie keeps rolling forward.
        setCookie(HOME_BROWSER_ID_COOKIE, existing, HOME_BROWSER_ID_DAYS);
        return existing;
    }
    const fresh = uuidv4();
    setCookie(HOME_BROWSER_ID_COOKIE, fresh, HOME_BROWSER_ID_DAYS);
    return fresh;
};

